import { html } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'

import { KlarnaBadge } from '../../assets/logos'
import addHyperlinks from '../helpers/addHyperlinks/addHyperlinks'
import styles from './style.scss'

function getCartSVG() {
  return `<svg aria-labelledby="osm-klarna-shopping-cart"
    viewBox="0 0 50 47" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M44.0879 34.8453L39.6426 12.4H34C34 6.88631 29.5137 2.39999 24 2.39999C18.4864 2.39999 14 6.88631 14 12.4H8.35745L3.91213 34.8443C3.44337 37.2018 4.04885 39.6188 5.57423 41.4762C7.09964 43.3346 9.35354 44.4 11.7578 44.4H36.2422C38.6465 44.4 40.9004 43.3346 42.4258 41.4762C43.9512 39.6188 44.5567 37.2018 44.0879 34.8453ZM24 6.39999C27.3086 6.39999 30 9.09139 30 12.4H18C18 9.09139 20.6914 6.39999 24 6.39999ZM39.334 38.9381C38.5703 39.8668 37.4434 40.4 36.2422 40.4H11.7579C10.5567 40.4 9.42974 39.8668 8.66606 38.9381C7.90434 38.0094 7.60159 36.8014 7.83401 35.6227L11.6426 16.4H36.3575L40.1661 35.6236C40.3985 36.8014 40.0957 38.0094 39.334 38.9381ZM29.6314 20.4H33.7979C32.8687 24.9586 28.8289 28.4 24 28.4C19.1712 28.4 15.1314 24.9586 14.2022 20.4H18.3687C19.1968 22.723 21.396 24.4 24 24.4C26.604 24.4 28.8033 22.723 29.6314 20.4Z" fill="#0B051D"/>
  </svg>`
}

function getWarningSVG() {
  return `<svg width="45" height="39" viewBox="0 0 45 39" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M44.3548 35.5604L24.4869 1.14527C24.0774 0.436114 23.3189 0 22.4998 0C21.6806 0 20.9222 0.436114 20.5126 1.14527L0.644762 35.5604C0.235193 36.2695 0.235193 37.1456 0.644762 37.8547C1.05433 38.5639 1.81279 39 2.63193 39H42.3714C43.1906 39 43.949 38.5639 44.3586 37.8547C44.7644 37.1418 44.7644 36.2695 44.3548 35.5604ZM6.60626 34.4113L22.4998 6.87923L38.3933 34.4113H6.60626Z" fill="#AA1E1E"/>
      <path d="M24.1267 28.5031C23.6906 28.1163 23.1521 27.9229 22.4998 27.9229C21.8513 27.9229 21.309 28.1163 20.8729 28.5031C20.4368 28.8899 20.2206 29.3829 20.2206 29.9821C20.2206 30.5813 20.4368 31.0743 20.8729 31.4611C21.3052 31.8479 21.8475 32.0413 22.4998 32.0413C23.1483 32.0413 23.6906 31.8479 24.1267 31.4611C24.559 31.0743 24.779 30.5813 24.779 29.9821C24.7752 29.3791 24.559 28.8861 24.1267 28.5031Z" fill="#AA1E1E"/>
      <path d="M20.869 26.2082H24.1911L24.7144 14.0918H20.3457L20.869 26.2082Z" fill="#AA1E1E"/>
    </svg>`
}

type Hyperlink = {
  text: string
  url: string
}

interface GetPaymentMethodsContentHTMLProps {
  infoPagePMGroups?: {
    title: string
    description: string
    hyperlinks?: Hyperlink[]
  }[]
}

function getPaymentMethodsContentHTML(props: GetPaymentMethodsContentHTMLProps): string {
  const { infoPagePMGroups } = props

  if (infoPagePMGroups?.length) {
    const pmGroupsHTML = infoPagePMGroups
      .map((pm) => {
        const { title, description, hyperlinks } = pm
        return `
        <div class="section-item payment-method">
          <h2 part="osm-heading">${title}</h2>
          <p class="content">
            ${addHyperlinks(description, hyperlinks)}
          </p>
        </div>
      `
      })
      .join('')
    return `${pmGroupsHTML}`
  }

  return ''
}

type FAQItem = {
  question: string
  answer: string
  hyperlinks?: Hyperlink[]
}

type PaymentMethodGroupWithFAQ = {
  title: string
  description: string
  hyperlinks?: Hyperlink[]
  faq: FAQItem[]
}

interface GetFaqContentHTMLProps {
  infoPageFAQs?: FAQItem[]
  infoPagePMGroups?: PaymentMethodGroupWithFAQ[]
}

function getFaqContentHTML(props: GetFaqContentHTMLProps) {
  let faqItems: FAQItem[] = []

  const { infoPageFAQs, infoPagePMGroups } = props

  if (infoPageFAQs?.length) faqItems = [...faqItems, ...infoPageFAQs]

  if (infoPagePMGroups?.length)
    infoPagePMGroups.forEach((pm) => (faqItems = [...faqItems, ...pm.faq]))

  const faqHtml = faqItems
    .map((item) => {
      const { question, answer, hyperlinks } = item
      return `
      <h3 part="osm-heading" class="content question">${question}</h3>
      <p class="content">${addHyperlinks(answer, hyperlinks)}</p>
    `
    })
    .join('')
  return `<div>${faqHtml}</div>`
}

function getMobileSVG() {
  return `
  <svg aria-hidden="true" viewBox="0 0 50 47" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.9922 37C21.9922 35.281 22.7111 34.3475 23.988 34.3475C25.2649 34.3475 25.9922 35.281 25.9922 37C25.9922 38.719 25.2649 39.6525 23.988 39.6525C22.7111 39.6525 21.9922 38.719 21.9922 37ZM31.9922 46H15.9922C11.5801 46 7.99219 42.4121 7.99219 38V9.99999C7.99219 5.58789 11.5801 1.99998 15.9922 1.99998H31.9922C36.4043 1.99998 39.9922 5.58789 39.9922 9.99999V38C39.9922 42.4121 36.4043 46 31.9922 46ZM15.9922 5.99999C13.7871 5.99999 11.9922 7.79491 11.9922 9.99999V38C11.9922 40.2051 13.7871 42 15.9922 42H31.9922C34.1973 42 35.9922 40.2051 35.9922 38V9.99999C35.9922 7.79491 34.1973 5.99999 31.9922 5.99999H15.9922Z" fill="#0B051D"/>
  </svg>`
}

type FAQ = {
  question: string
  answer: string
  hyperlinks?: Hyperlink[]
}

export type PaymentMethodGroup = {
  title: string
  description: string
  hyperlinks?: Hyperlink[]
  faq: FAQ[]
}

interface AvailablePaymentMethodsSectionProps {
  assetsUrl: string
  infoPagePaymentSectionDescription: string
  infoPagePaymentSectionTitle: string
  infoPagePMGroups: PaymentMethodGroup[]
}

function renderAvaialblePaymentMethodsSection(props: AvailablePaymentMethodsSectionProps) {
  const {
    assetsUrl,
    infoPagePaymentSectionDescription,
    infoPagePaymentSectionTitle,
    infoPagePMGroups,
  } = props

  return unsafeHTML(`
      <section class="columns">
          <div class="section-item">
          <div class="section-image">
              <img loading="lazy" alt="Klarna Checkout is easy, convenient and fast"
              src="${assetsUrl}/images/info-page-hand-mobile.webp"/>
          </div>
          </div>

          <div class="section-item">
              <h2 part="osm-heading">${infoPagePaymentSectionTitle}</h2>
              <p class="content">${infoPagePaymentSectionDescription}</p>
          </div>

          ${getPaymentMethodsContentHTML({ infoPagePMGroups })}<br />
      </section>
  `)
}

interface JourneySectionProps {
  infoPageJourneyTitle: string
  infoPageJourneyStepOne: string
  infoPageJourneyStepTwo: string
  infoPageJourneyStepThree: string
}

function renderJourneySection(props: JourneySectionProps) {
  const {
    infoPageJourneyTitle,
    infoPageJourneyStepOne,
    infoPageJourneyStepTwo,
    infoPageJourneyStepThree,
  } = props

  return unsafeHTML(`
      <section class="journey">
          <h2 part="osm-heading">${infoPageJourneyTitle}</h2>
          <div class="journey-steps-wrapper">
          <div class="journey-step">
              <div class="icon">${getCartSVG()}</div>
              <p class="content">${infoPageJourneyStepOne}</p>
          </div>

          <div class="journey-step">
              <div class="icon">${KlarnaBadge({ width: 95, height: 40 })}</div>
              <p class="content">${infoPageJourneyStepTwo}</p>
          </div>

          <div class="journey-step">
              <div class="icon">${getMobileSVG()}</div>
              <p class="content">${infoPageJourneyStepThree}</p>
          </div>

          <div class="journey-step"></div>
          </div>
      </section>
  `)
}

interface GenericContentSectionProps {
  infoPageTitleTertiaryFirstColumn: string
  infoPageContentTertiaryFirstColumn: string
  infoPageTitleTertiarySecondColumn: string
  infoPageContentTertiarySecondColumn: string
}

function renderGenericContentSection(props: GenericContentSectionProps) {
  const {
    infoPageTitleTertiaryFirstColumn,
    infoPageContentTertiaryFirstColumn,
    infoPageTitleTertiarySecondColumn,
    infoPageContentTertiarySecondColumn,
  } = props

  return unsafeHTML(`
      <section class="columns">
          <div class="section-item">
          <h2 part="osm-heading">${infoPageTitleTertiaryFirstColumn}</h2>
          <p class="content">${infoPageContentTertiaryFirstColumn}</p>
          </div>

          <div class="section-item">
          <h2 part="osm-heading">${infoPageTitleTertiarySecondColumn}</h2>
          <p class="content">${infoPageContentTertiarySecondColumn}</p>
          </div>
      </section>
  `)
}

interface FAQSectionProps {
  assetsUrl: string
  infoPageTitleFirstColumn: string
  infoPageContentFirstColumn: string
  infoPageTitleSecondColumn: string
  infoPageContentSecondColumn: string
  infoPageFAQs: FAQ[]
  infoPagePMGroups: PaymentMethodGroup[]
}

function renderFAQSection(props: FAQSectionProps) {
  const {
    assetsUrl,
    infoPageTitleFirstColumn,
    infoPageContentFirstColumn,
    infoPageTitleSecondColumn,
    infoPageContentSecondColumn,
    infoPageFAQs,
    infoPagePMGroups,
  } = props

  return unsafeHTML(`
  <section class="columns">
      <div class="section-item">
          <div class="section-image">
              <img loading="lazy" alt="Manage your purchases through the Klarna app"
              src="${assetsUrl}/images/info-page-help-green.webp"/>
          </div>
          <h2 part="osm-heading">${infoPageTitleFirstColumn}</h2>
          <p class="content">${infoPageContentFirstColumn}</p>
          ${getFaqContentHTML({ infoPageFAQs, infoPagePMGroups })}
      </div>

      <div class="section-item">
          <div class="section-image">
              <img loading="lazy" alt="Klarna makes shopping smoooth"
              src="${assetsUrl}/images/info-page-about-klarna-with-headphone.webp"/>
          </div>
          <h2 part="osm-heading">
              ${infoPageTitleSecondColumn}
          </h2>
          <p class="content">${infoPageContentSecondColumn}</p>
      </div>
  </section>
  `)
}

function renderRiskDisclaimer(
  riskDisclaimerSource = '',
  riskDisclaimerHeading = '',
  riskDisclaimerSubHeading = ''
) {
  if (!riskDisclaimerSource) return ''

  return unsafeHTML(
    `
    <section class="columns">
      <div class="section-item">
        <div class="divider">${riskDisclaimerSource}</div>

        <div class="risk-disclosure-container">
          <div class="warning-container">${getWarningSVG()}</div>
          <div>
            <b>${riskDisclaimerHeading}</b>
            <p>${riskDisclaimerSubHeading}</p>
          </div>
        </div>
      </div>

    </section>
    `
  )
}

interface InfoPageProps {
  theme: string
  infoPageMainTitle: string
  infoPageMainSubtitle: string
  assetsUrl: string
  infoPageTitleFirstColumn: string
  infoPageContentFirstColumn: string
  infoPageTitleSecondColumn: string
  infoPageContentSecondColumn: string
  infoPageTitleTertiaryFirstColumn: string
  infoPageContentTertiaryFirstColumn: string
  infoPageTitleTertiarySecondColumn: string
  infoPageContentTertiarySecondColumn: string
  fiscRiskBasedDisclaimerSource: string
  fiscRiskBasedDisclaimerHeading: string
  fiscRiskBasedDisclaimerSubheading: string
  infoPageJourneyTitle: string
  infoPageJourneyStepOne: string
  infoPageJourneyStepTwo: string
  infoPageJourneyStepThree: string
  infoPagePaymentSectionDescription: string
  infoPagePaymentSectionTitle: string
  infoPageFAQs: FAQItem[]
  infoPagePMGroups: PaymentMethodGroupWithFAQ[]
}
export default function infoPage(props: InfoPageProps) {
  if (Object.keys(props).length === 0) return

  const {
    theme,
    infoPageMainTitle,
    infoPageMainSubtitle,
    assetsUrl,
    infoPageTitleFirstColumn,
    infoPageContentFirstColumn,
    infoPageTitleSecondColumn,
    infoPageContentSecondColumn,
    infoPageTitleTertiaryFirstColumn,
    infoPageContentTertiaryFirstColumn,
    infoPageTitleTertiarySecondColumn,
    infoPageContentTertiarySecondColumn,
    fiscRiskBasedDisclaimerSource,
    fiscRiskBasedDisclaimerHeading,
    fiscRiskBasedDisclaimerSubheading,
    infoPageJourneyTitle,
    infoPageJourneyStepOne,
    infoPageJourneyStepTwo,
    infoPageJourneyStepThree,
    infoPagePaymentSectionDescription,
    infoPagePaymentSectionTitle,
    infoPageFAQs,
    infoPagePMGroups,
  } = props

  return html`
    <style>
      ${styles}
    </style>
    <div
      data-testid="${infoPageMainTitle ? 'info-page' : ''}"
      class="container ${theme}"
      part="osm-container"
    >
      <div class="landing-page">
        <h1 part="osm-heading">${infoPageMainTitle}</h1>
        <p class="content">${infoPageMainSubtitle}</p>

        ${renderAvaialblePaymentMethodsSection({
          assetsUrl,
          infoPagePaymentSectionDescription,
          infoPagePaymentSectionTitle,
          infoPagePMGroups,
        })}
        ${renderJourneySection({
          infoPageJourneyTitle,
          infoPageJourneyStepOne,
          infoPageJourneyStepTwo,
          infoPageJourneyStepThree,
        })}
        ${renderGenericContentSection({
          infoPageTitleTertiaryFirstColumn,
          infoPageContentTertiaryFirstColumn,
          infoPageTitleTertiarySecondColumn,
          infoPageContentTertiarySecondColumn,
        })}
        ${renderFAQSection({
          assetsUrl,
          infoPageTitleFirstColumn,
          infoPageContentFirstColumn,
          infoPageTitleSecondColumn,
          infoPageContentSecondColumn,
          infoPageFAQs,
          infoPagePMGroups,
        })}
        ${renderRiskDisclaimer(
          fiscRiskBasedDisclaimerSource,
          fiscRiskBasedDisclaimerHeading,
          fiscRiskBasedDisclaimerSubheading
        )}
      </div>
    </div>
  `
}
